import { useQuery } from "@tanstack/react-query";
import { Boxes, CheckCircle2, Factory, Image, FileText, Users, Mail, Activity, Loader2 } from "lucide-react";
import { adminApi } from "./adminApi";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-2xl glass p-5" data-testid={`stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className={`mb-3 flex h-11 w-11 items-center justify-center rounded-xl ${accent}`}><Icon className="h-5 w-5" /></div>
      <p className="font-head text-3xl font-extrabold text-white">{value}</p>
      <p className="text-sm text-slate-400">{label}</p>
    </div>
  );
}

const EVENT_LABELS = {
  session_started: "Sessions started", solution_opened: "Solutions opened", video_played: "Videos played",
  pdf_opened: "PDFs opened", document_requested: "Documents emailed", industry_opened: "Industries opened",
  contact_opened: "Contact views", journey_opened: "Journey views", session_completed: "Sessions completed",
  session_timeout: "Session timeouts",
};

export default function Dashboard() {
  const { data, isLoading } = useQuery({ queryKey: ["admin-dashboard"], queryFn: () => adminApi.dashboard() });

  if (isLoading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-cyan-400" /></div>;
  const d = data || {};

  return (
    <div data-testid="admin-dashboard-overview">
      <h1 className="font-head text-3xl font-extrabold text-white">Dashboard</h1>
      <p className="mt-1 text-slate-400">Live overview of your kiosk content and visitor activity.</p>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat icon={Boxes} label="Total Solutions" value={d.total_solutions ?? 0} accent="cyan-grad text-[#041021]" />
        <Stat icon={CheckCircle2} label="Published" value={d.published_solutions ?? 0} accent="bg-emerald-500/20 text-emerald-300" />
        <Stat icon={Factory} label="Industries" value={d.total_industries ?? 0} accent="bg-blue-500/20 text-blue-300" />
        <Stat icon={Image} label="Media Files" value={d.media_count ?? 0} accent="bg-violet-500/20 text-violet-300" />
        <Stat icon={FileText} label="Documents" value={d.pdf_count ?? 0} accent="bg-amber-500/20 text-amber-300" />
        <Stat icon={Activity} label="Visitor Sessions" value={d.sessions ?? 0} accent="bg-cyan-500/20 text-cyan-300" />
        <Stat icon={Mail} label="Doc Requests" value={d.doc_requests ?? 0} accent="bg-rose-500/20 text-rose-300" />
        <Stat icon={CheckCircle2} label="Published Ind." value={d.published_industries ?? 0} accent="bg-emerald-500/20 text-emerald-300" />
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl glass p-6">
          <h2 className="font-head text-lg font-bold text-white">Interaction Events</h2>
          <div className="mt-4 space-y-2">
            {Object.keys(d.event_counts || {}).length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-500">No visitor activity recorded yet.</p>
            ) : Object.entries(d.event_counts).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between rounded-lg bg-[#0b1730] px-4 py-2.5">
                <span className="text-sm text-slate-300">{EVENT_LABELS[k] || k}</span>
                <span className="font-tele text-sm font-bold text-cyan-300">{v}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl glass p-6">
          <h2 className="font-head text-lg font-bold text-white">Most Viewed Solutions</h2>
          <div className="mt-4 space-y-2">
            {(d.top_solutions || []).length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-500">No solution views yet.</p>
            ) : d.top_solutions.map((s, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg bg-[#0b1730] px-4 py-2.5">
                <span className="text-sm text-slate-200">{s.name}</span>
                <span className="font-tele text-sm font-bold text-cyan-300">{s.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
